import { NSARouterMakerResult } from "namirasoft-account-react";
import { FilterItem, FilterItemOperator, NamingConvention } from "namirasoft-core";
import { ContainerConnectType, ContainerInputRow, ContainerRow, ContainerType, EnvironmentRow, ProjectRow, ServerRow } from "namirasoft-infra";
import { LogGroupRow } from "namirasoft-log";
import { NamirasoftMap } from "namirasoft-site-map";
import { NSBox, NSBoxEntity, NSBoxEnum, NSBoxString, NSBoxTextArea, NSColumn } from "namirasoft-site-react";
import { Component, createRef } from "react";
import { InfraEntities } from "../InfraEntities";
import { InfraLayout } from '../InfraLayout';
import { InfraRouterProps } from "../InfraRouterProps";

interface ContainerEditPageProps extends NSARouterMakerResult, InfraRouterProps
{
    isEdit: boolean;
}

interface ContainerEditPageState
{
    selected_project_id?: string;
    selected_environment_id?: string;
    connect_type?: ContainerConnectType;
}

export default class ContainerEditPage extends Component<ContainerEditPageProps, ContainerEditPageState>
{
    NSBoxString_Name = createRef<NSBoxString>();
    NSBoxEnum_Type = createRef<NSBoxEnum<ContainerType>>();
    NSBoxEnum_ConnectType = createRef<NSBoxEnum<ContainerConnectType>>();
    NSBoxEntity_ConnectServerID = createRef<NSBoxEntity<ServerRow>>();
    NSBoxEntity_ProjectID = createRef<NSBoxEntity<ProjectRow>>();
    NSBoxEntity_EnvironmentID = createRef<NSBoxEntity<EnvironmentRow>>();
    NSBoxEntity_LogGroupID = createRef<NSBoxEntity<LogGroupRow>>();
    NSBoxTextArea_Description = createRef<NSBoxTextArea>();

    constructor(props: ContainerEditPageProps)
    {
        super(props);
        this.state = {}
    }

    override render()
    {
        let namirasoft = new NamirasoftMap(this.props.account.token_manager, console.error);

        return (
            <InfraLayout<ContainerRow, ContainerInputRow>
                {...this.props}
                pages={{
                    entity: InfraEntities.container(this.props),
                    edit: {
                        ...this.props,
                        ui: {
                            tab_page_edit: {
                                editor: {
                                    getInputRow: (): ContainerInputRow =>
                                    {
                                        let name = this.NSBoxString_Name.current?.getValue() ?? "";
                                        let type = this.NSBoxEnum_Type.current?.getValueEnumOne(ContainerType, ContainerType.Docker) ?? ContainerType.Docker;
                                        let connect_type = this.NSBoxEnum_ConnectType.current?.getValueEnumOne(ContainerConnectType, ContainerConnectType.ByServer) ?? ContainerConnectType.ByServer;
                                        let connect_server_id = this.NSBoxEntity_ConnectServerID.current?.getValueOne(this.state.connect_type === ContainerConnectType.ByServer) ?? null;
                                        let project_id = this.NSBoxEntity_ProjectID.current?.getValueOne() ?? "";
                                        let environment_id = this.NSBoxEntity_EnvironmentID.current?.getValueOne() ?? "";
                                        let log_group_id = this.NSBoxEntity_LogGroupID.current?.getValueOne() ?? null;
                                        let description = this.NSBoxTextArea_Description.current?.getValue() ?? "";

                                        return {
                                            workspace_id: "",
                                            name,
                                            type,
                                            connect_type,
                                            connect_server_id,
                                            project_id,
                                            environment_id,
                                            log_group_id,
                                            container_cli_server_id: "",
                                            description,
                                            container_category: [],
                                            container_field: [],
                                            container_tag: [],
                                        };
                                    },
                                    setRow: (row: ContainerRow) =>
                                    {
                                        this.NSBoxString_Name.current?.setValue(row.name);
                                        this.NSBoxEnum_Type.current?.setValueOne(row.type);
                                        this.NSBoxEnum_ConnectType.current?.setValueOne(row.connect_type);
                                        this.NSBoxEntity_ProjectID.current?.setValueOne(row.project_id);
                                        this.NSBoxEntity_LogGroupID.current?.setValueOne(row.log_group_id);
                                        this.NSBoxTextArea_Description.current?.setValue(row.description);

                                        this.setState(
                                            () => ({
                                                selected_project_id: row.project_id,
                                                selected_environment_id: row.environment_id,
                                                connect_type: row.connect_type,
                                            }),
                                            () =>
                                            {
                                                this.NSBoxEntity_EnvironmentID.current?.setValueOne(row.environment_id);
                                                this.NSBoxEntity_ConnectServerID.current?.setValueOne(row.connect_server_id);
                                            }
                                        );
                                    }
                                },
                                ui: {
                                    extra_tabs: [
                                        {
                                            title: "Connect",
                                            clicked: true,
                                            getContent: () => (
                                                <NSColumn>
                                                    <NSBoxEnum<ContainerConnectType>
                                                        ref={this.NSBoxEnum_ConnectType}
                                                        title="Connect Type"
                                                        required
                                                        multiple={false}
                                                        getEnumObject={() => ContainerConnectType}
                                                        getTitle={(item) => NamingConvention.auto.convert(item, NamingConvention.Pascal_Case_Space)}
                                                        onChanged={(box) => this.setState(() => ({ connect_type: box.getValueOne() as ContainerConnectType }))}
                                                        style={{ width: NSBox.width.double }}
                                                    />
                                                    {
                                                        this.state.connect_type === ContainerConnectType.ByServer &&
                                                        <>
                                                            <NSBoxEntity<ServerRow>
                                                                ref={this.NSBoxEntity_ConnectServerID}
                                                                title="Connect Server"
                                                                required
                                                                multiple={false}
                                                                table_name="server"
                                                                list={async (filters, page, size, sorts) =>
                                                                {
                                                                    if (!filters)
                                                                        filters = [];

                                                                    if (this.state.selected_environment_id)
                                                                        filters.push(new FilterItem(
                                                                            namirasoft.infra.tables.server,
                                                                            namirasoft.infra.tables.server.columns.environment_id,
                                                                            false,
                                                                            FilterItemOperator.all.equals,
                                                                            this.state.selected_environment_id,
                                                                        ));

                                                                    return await this.props.servers.infra.server.List(filters, page, size, sorts, null);
                                                                }}
                                                                getValue={(item) => item.id}
                                                                getTitle={(item) => item.name}
                                                                getSort={() => null}
                                                                style={{ width: NSBox.width.double }}
                                                            />
                                                            {/* TODO: Replace with correct container connect-type images */}
                                                            <img
                                                                style={{ width: "100%", maxWidth: NSBox.width.double }}
                                                                src="https://static.namirasoft.com/image/namirasoft/infra/diagram/database/connect-type/by-server.png"
                                                                alt="By Server"
                                                            />
                                                        </>
                                                    }
                                                </NSColumn>
                                            )
                                        }
                                    ]
                                }
                            }
                        }
                    }
                }}
            >
                <NSColumn>
                    <NSBoxString
                        ref={this.NSBoxString_Name}
                        title="Name"
                        required
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxEnum<ContainerType>
                        ref={this.NSBoxEnum_Type}
                        title="Type"
                        required
                        multiple={false}
                        getEnumObject={() => ContainerType}
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxEntity<ProjectRow>
                        ref={this.NSBoxEntity_ProjectID}
                        title="Project"
                        required
                        multiple={false}
                        table_name="project"
                        table={namirasoft.infra.tables.project}
                        getValue={(item) => item.id}
                        getTitle={(item) => item.name}
                        getSort={() => null}
                        onChanged={(box) =>
                        {
                            this.setState(
                                () => ({ selected_project_id: box.getValueOne() ?? undefined }),
                                () =>
                                {
                                    this.NSBoxEntity_EnvironmentID.current?.setValue(null);
                                    this.NSBoxEntity_EnvironmentID.current?.reload();
                                },
                            )
                        }}
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxEntity<EnvironmentRow>
                        ref={this.NSBoxEntity_EnvironmentID}
                        title="Environment"
                        required
                        multiple={false}
                        table_name="environment"
                        list={async (filters, page, size, sorts) =>
                        {
                            if (!filters)
                                filters = [];

                            if (this.state.selected_project_id)
                                filters.push(new FilterItem(
                                    namirasoft.infra.tables.environment,
                                    namirasoft.infra.tables.environment.columns.project_id,
                                    false,
                                    FilterItemOperator.all.equals,
                                    this.state.selected_project_id,
                                ));

                            return await this.props.servers.infra.environment.List(filters, page, size, sorts, null);
                        }}
                        getValue={(item) => item.id}
                        getTitle={(item) => item.name}
                        getSort={() => null}
                        onChanged={(box) =>
                        {
                            this.setState(
                                () => ({ selected_environment_id: box.getValueOne(false) ?? "" }),
                                () =>
                                {
                                    this.NSBoxEntity_ConnectServerID.current?.setValue(null);
                                    this.NSBoxEntity_ConnectServerID.current?.reload();
                                },
                            )
                        }}
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxEntity<LogGroupRow>
                        ref={this.NSBoxEntity_LogGroupID}
                        title="Log Group"
                        required={false}
                        multiple={false}
                        table_name="log-group"
                        table={namirasoft.log.tables.log_group}
                        getValue={(item) => item.id}
                        getTitle={(item) => item.name}
                        getSort={() => null}
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxTextArea
                        ref={this.NSBoxTextArea_Description}
                        required
                        title="Description"
                        style={{ width: NSBox.width.double }}
                        min_length={50}
                    />
                </NSColumn>
            </InfraLayout>
        );
    }
}